import * as S from "./styles.ts";
import { createEditor, Editor, Element, Node, Transforms } from "slate";
import { useMemo } from "react";
import { Slate, withReact } from "slate-react";
import EditorContent from "@admin/config/overrides/fieldTypes/MarkdownEditor/children/EditorContent";
import Toolbar from "@admin/config/overrides/fieldTypes/MarkdownEditor/children/Toolbar";

const defaultValue = (value: string | any[] = "") => {
  if (typeof value !== "string") return value;

  return [
    {
      type: "paragraph",
      children: [{ text: value }],
    },
  ];
};

export function installListNormalizer(editor: Editor) {
  const { normalizeNode } = editor;

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    // 1️⃣ Ensure every list-item starts with a paragraph
    if (Element.isElement(node) && (node as any).type === "list-item") {
      const first = node.children[0];
      if (
        !first ||
        !Element.isElement(first) ||
        (first as any).type !== "paragraph"
      ) {
        (Transforms as any).insertNodes(
          editor,
          { type: "paragraph", children: [{ text: "" }] },
          { at: path.concat(0) }
        );
        return;
      }
    }

    // 2️⃣ Ensure lists (<ol>/<ul>) contain only <list-item> *or* nested <ol>/<ul>
    if (Element.isElement(node) && ["ol", "ul"].includes((node as any).type)) {
      for (const [child, childPath] of Node.children(editor, path)) {
        if (!Element.isElement(child)) continue;

        // ✅ Skip if child is a nested list (<ol> or <ul>)
        if (["ol", "ul"].includes((child as any).type)) continue;

        // ✅ Convert only non-list, non-list-item children into list-items
        if ((child as any).type !== "list-item") {
          (Transforms as any).setNodes(
            editor,
            { type: "list-item" },
            { at: childPath }
          );
          return;
        }
      }
    }

    // 3️⃣ Ensure nested <ol>/<ul> appear only inside list-items
    if (Element.isElement(node) && ["ol", "ul"].includes((node as any).type)) {
      try {
        const parent = Node.parent(editor, path);
        if (Element.isElement(parent) && (parent as any).type !== "list-item") {
          (Transforms as any).wrapNodes(
            editor,
            { type: "list-item", children: [] },
            { at: path }
          );
          return;
        }
      } catch {
        // root-level list is fine
      }
    }

    normalizeNode(entry);
  };
}

const TextEditor = ({ value, onChange, disabled }) => {
  const editor = useMemo(() => {
    const e = withReact(createEditor());
    installListNormalizer(e);
    Editor.marks(e) &&
      Object.keys(Editor.marks(e) || {}).forEach((k) =>
        Editor.removeMark(e, k)
      );
    return e;
  }, []);

  return (
    <>
      <S.TextEditorTips>
        <b>
          <u>Tips:</u>
        </b>
        <br />
        Exit a link: Click outside link or use arrow keys to move outside of
        link. <br />
        Edit a link: Click on link text and use the toolbar fields to edit URL
        and title. <br />
        Variant Word: Text displayed will change based site version viewed eg
        GBP or USD. <br />
        <b>
          <u>Keyboard shortcuts for List Items: </u>
        </b>
        <br />
        Indent List: Press Tab
        <br />
        Un-Indent List: Press Shift + Tab
        <br />
        Exit List: Press Enter/Return.
      </S.TextEditorTips>
      <Slate
        editor={editor}
        initialValue={defaultValue(value)}
        onChange={(val) => (disabled ? null : onChange(val))}
      >
        <S.TextEditorContainer data-markdown-editor="true">
          <Toolbar disabled={disabled} />
          <EditorContent disabled={disabled} />
        </S.TextEditorContainer>
      </Slate>
    </>
  );
};

export default TextEditor;
